package ru.villawonka.app.presentation.screens.search.compose.view

import androidx.compose.foundation.*
import androidx.compose.foundation.layout.*
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material.*
import androidx.compose.runtime.Composable
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.graphics.ColorFilter
import androidx.compose.ui.graphics.ColorMatrix
import androidx.compose.ui.unit.dp
import org.jetbrains.compose.resources.DrawableResource
import org.jetbrains.compose.resources.StringResource
import org.jetbrains.compose.resources.painterResource
import org.jetbrains.compose.resources.stringResource
import org.jetbrains.compose.ui.tooling.preview.Preview
import ru.villawonka.app.presentation.design.components.filledBadgeBackgroundInfo
import ru.villawonka.app.presentation.design.components.filledBadgeContentInverted
import ru.villawonka.app.presentation.design.extensions.AppSpacer
import ru.villawonka.app.presentation.design.theme.AppColors
import ru.villawonka.app.presentation.design.theme.AppTextStyles
import ru.villawonka.app.presentation.design.theme.AppTheme
import ru.villawonka.app.resources.AppResources
import villawonka.composeapp.generated.resources.*

@Composable
fun TabBar(
    selectedIndex: Int,
    items: List<TabItem>,
    onClickListener: ((Int) -> Unit)?
) {
    val scrollState = rememberScrollState()

    Row(
        modifier = Modifier
            .fillMaxWidth()
            .horizontalScroll(scrollState)
    ) {
        items.forEachIndexed { index, tab ->
            if (index == 0) AppSpacer(16.dp)
            TabButton(
                tab = tab,
                selected = index == selectedIndex,
                onClickListener = { if (tab.enabled) onClickListener?.invoke(index) }
            )

            if (index == items.size - 1) {
                AppSpacer(16.dp)
            } else {
                AppSpacer(8.dp)
            }

        }
    }
}

@OptIn(ExperimentalMaterialApi::class)
@Composable
private fun TabButton(
    tab: TabItem,
    selected: Boolean,
    onClickListener: () -> Unit
) {
    Surface(
        onClick = onClickListener,
        shape = RoundedCornerShape(16.dp),
        color = MaterialTheme.colors.filledTabsBackgroundEnabled,
        border = if (selected) {
            BorderStroke(
                width = 2.dp,
                color = MaterialTheme.colors.filledTabsOutlineFocused
            )
        } else {
            null
        }
    ) {
        Box {
            tab.badge?.let {
                Box(
                    modifier = Modifier
                        .background(
                            color = MaterialTheme.colors.filledBadgeBackgroundInfo,
                            shape = RoundedCornerShape(
                                topEnd = 16.dp,
                                bottomStart = 16.dp
                            )
                        )
                        .padding(
                            vertical = 4.dp,
                            horizontal = 12.dp,
                        )
                        .align(Alignment.TopEnd)
                ) {
                    Text(
                        text = it.text,
                        style = AppTextStyles.labelSmallBold,
                        color = MaterialTheme.colors.filledBadgeContentInverted,
                    )
                }
            }
            Column(
                modifier = Modifier
                    .padding(12.dp)
            ) {
                Image(
                    painter = painterResource(tab.image),
                    contentDescription = null,
                    modifier = Modifier.size(60.dp),
                    colorFilter = if (tab.enabled) {
                        null
                    } else {
                        ColorFilter.colorMatrix(
                            ColorMatrix().apply {
                                setToSaturation(0f)
                            }
                        )
                    }
                )
                AppSpacer(4.dp)
                Box(
                    modifier = Modifier.defaultMinSize(
                        minWidth = 96.dp
                    )
                ) {
                    Text(
                        text = tab.title,
                        style = AppTextStyles.labelMediumRegular,
                        color = MaterialTheme.colors.filledTabsContentEnabled
                    )
                }
            }
        }
    }
}

data class TabItem(
    val id: Int,
    val title: String,
    val image: DrawableResource,
    val badge: TabBadge? = null,
    val enabled: Boolean = true,
)

data class TabBadge(
    val text: String,
)

val Colors.filledTabsContentEnabled
    get() = if (isLight) AppColors.grayLight950 else AppColors.grayDark50

val Colors.filledTabsContentFocused
    get() = if (isLight) AppColors.blue600 else AppColors.blue50

val Colors.filledTabsBackgroundEnabled
    get() = if (isLight) AppColors.grayLight100 else AppColors.grayDark800

val Colors.filledTabsBackgroundFocused
    get() = if (isLight) AppColors.grayLight100 else AppColors.grayDark800

val Colors.filledTabsBackgroundIcon
    get() = if (isLight) AppColors.violet500 else AppColors.violet500

val Colors.filledTabsOutlineFocused
    get() = if (isLight) AppColors.violet500 else AppColors.violet500


@Preview
@Composable
private fun Preview() {
    val tabs = listOf(
        TabItem(
            id = 0,
            title = AppResources.strings.tabBarItemProperty,
            image = Res.drawable.category_housing,
        ),
        TabItem(
            id = 1,
            title = AppResources.strings.tabBarItemTransport,
            image = Res.drawable.category_transport,
            enabled = false,
            badge = TabBadge(AppResources.strings.tabBarSoonBadge)
        ),
    )

    Column {
        AppTheme(isDarkTheme = false) {
            Box(
                Modifier
                    .background(Color.White)
                    .padding(16.dp)
            ) {
                TabBar(
                    selectedIndex = 0,
                    items = tabs,
                    onClickListener = {}
                )
            }
        }
        AppTheme(isDarkTheme = true) {
            Box(
                Modifier
                    .background(Color.Black)
                    .padding(16.dp)
            ) {
                TabBar(
                    selectedIndex = 0,
                    items = tabs,
                    onClickListener = {}
                )
            }
        }
    }
}